import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./styles/app-polish.css";
import styled from "styled-components";
import { Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useSignIn } from "react-auth-kit";
import { registerApplyTokens } from "./auth/authBridge";
import { track } from "./utils/track";
import { Login } from "./components/login/login";
import { Register } from "./components/register";
import { EmailVerified } from "./components/email-verified/EmailVerified";
import { Home } from "./components/home/home";
import { MapPage } from "./components/map/MapPage";
import { Account } from "./components/user/Account";
import { BillingReturn } from "./components/billing/BillingReturn";
import { Admin } from "./components/admin/Admin";
import { Analytics } from "./components/analytics/Analytics";
import { ArticleListPage, ArticlePage } from "./components/articles/ArticlePages";
import {
  ForumCategoryPage,
  ForumHome,
  ForumThreadPage,
  NewThreadPage,
} from "./components/forum/ForumPages";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RedirectIfAuth } from "./components/auth/RedirectIfAuth";
import { UpgradeNotice } from "./components/common/UpgradeNotice";
import { SessionExpiredNotice } from "./components/common/SessionExpiredNotice";

const AppContainer = styled.div`
  width: 100%;
  /* min-height (not height:100%): a fixed one-viewport height would cap the
     sticky navbar's containing block, so it scrolls off after one screen on
     mobile. min-height lets the container grow with content. */
  min-height: 100vh;
`;

const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    track("page_view", location.pathname);
  }, [location.pathname]);
  return null;
};

// Wires apiService's on-demand token refresh (authBridge) to react-auth-kit's
// signIn, so a refreshed access token (e.g. after a plan change) is written back
// to the auth cookie/state and used by subsequent requests.
const AuthBridge = () => {
  const signIn = useSignIn();
  useEffect(() => {
    registerApplyTokens(({ accessToken, refreshToken, account }) => {
      signIn({
        token: accessToken,
        expiresIn: 60 * 24 * 7,
        tokenType: "Bearer",
        refreshToken,
        refreshTokenExpireIn: 60 * 24 * 30,
        authState: {
          id: account.id,
          email: account.email,
          username: account.username,
          image_link: account.image_link,
          roles: account.roles || [],
          permissions: account.permissions || [],
          permission_version: account.permission_version,
        },
      });
    });
    return () => registerApplyTokens(null);
  }, [signIn]);
  return null;
};

const App = () => {
  return (
    <AppContainer>
      <PageViewTracker />
      <AuthBridge />
      <UpgradeNotice />
      <SessionExpiredNotice />
      <Routes>
        {/* Home page - supports query params:
            ?doc=<documentId>&prediction=<predictionId>
            - doc: opens document modal
            - prediction: opens AI prediction reasoning modal on chart */}
        <Route
          path="/"
          element={
            <RequireAuth loginPath="/login">
              <Home />
            </RequireAuth>
          }
        ></Route>
        {/* The marketing/landing page lives on the apex Astro site
            (spotprisprognos.se); the app (app.spotprisprognos.se) has no /welcome.
            Unauthenticated visitors to "/" are sent to /login. */}
        {/* Account page - logged in user's own profile with settings */}
        <Route
          path="/account"
          element={
            <RequireAuth loginPath="/login">
              <Account />
            </RequireAuth>
          }
        ></Route>
        {/* Stripe checkout/portal return — ?status=success|cancelled */}
        <Route
          path="/account/billing"
          element={
            <RequireAuth loginPath="/login">
              <BillingReturn />
            </RequireAuth>
          }
        ></Route>
        {/* Map page - bidding-zone map with events + generation assets */}
        <Route
          path="/map"
          element={
            <RequireAuth loginPath="/login">
              <MapPage />
            </RequireAuth>
          }
        ></Route>
        {/* Forum (also readable, without login, on the marketing site) */}
        {[
          ["/forum", <ForumHome />],
          ["/forum/c/:slug", <ForumCategoryPage />],
          ["/forum/t/:id", <ForumThreadPage />],
          ["/forum/ny", <NewThreadPage />],
          // Artiklar: admin-only while in testing (the API checks articles:read).
          ["/artiklar", <ArticleListPage />],
          ["/artiklar/:slug", <ArticlePage />],
        ].map(([path, page]) => (
          <Route
            key={path as string}
            path={path as string}
            element={<RequireAuth loginPath="/login">{page as JSX.Element}</RequireAuth>}
          />
        ))}
        {/* Analytics page - prediction validation analytics */}
        <Route
          path="/analytics"
          element={
            <RequireAuth loginPath="/login">
              <Analytics />
            </RequireAuth>
          }
        ></Route>
        {/* Admin page - role management (admin only) */}
        <Route
          path="/admin"
          element={
            <RequireAuth loginPath="/login">
              <Admin />
            </RequireAuth>
          }
        ></Route>
        <Route
          path="/login"
          element={
            <RedirectIfAuth>
              <Login />
            </RedirectIfAuth>
          }
        ></Route>
        <Route
          path="/register"
          element={
            <RedirectIfAuth>
              <Register />
            </RedirectIfAuth>
          }
        ></Route>
        <Route path="/email-verified" element={<EmailVerified />}></Route>
      </Routes>
    </AppContainer>
  );
};

export default App;
